import React, { Fragment, useState, useContext, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  TextField,
  Divider,
  Container,
  InputAdornment,
  IconButton,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";

import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import SideBanner from "../components/layout/sidebanner";
import AuthContext from "../store/auth/auth-context";
import Cookie from "js-cookie";
import useHttp from "../hooks/use-http";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

let resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required("Please enter password")
    .min(8, "Password should have minimum of 8 characters")
    .max(60, "Password should have maximum of 50 characters"),
  passwordConfirmation: yup
    .string()
    .required("Please enter confirm password")
    .oneOf([yup.ref("password"), ""], "Passwords must match"),
});

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("resetToken");
  const {
    isLoading,
    isError,
    message,
    sendRequest: resetPasswordHandler,
  } = useHttp();

  const {
    isLoading: isTokenLoading,
    isError: isTokenError,
    message: tokenMessage,
    sendRequest: tokenHandler,
  } = useHttp();
  
  const [isResetPasswordState, setIsResetPasswordState] = useState(false);
  const navigate = useNavigate();
  const accessToken = Cookie.get("accessToken");
  const refreshToken = Cookie.get("refreshToken");

  useEffect(() => {
    if (accessToken && refreshToken) navigate("/");

    if (!resetToken) navigate("/login");

    tokenHandler(
      {
        URL: "/api/user/validatetoken",
        method: "POST",
        header: {
          "Content-Type": "application/json",
        },
        body: {
          token: resetToken,
        },
      },(tokenRes: any)=>{
        // console.log(tokenRes)
          if (tokenRes.error) navigate("/login");
      }
    )

  }, []);

  const {
    register: resetPassword,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show: any) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const resetPasswordResponse = (res: any) => {
    // console.log(res);
    if (res.error === false) {
      setIsResetPasswordState(true);
    }
  };

  const onSubmit = (data: any) => {
    // console.log(123)
    setIsResetPasswordState(false);
    if (data.password && resetToken) {
      resetPasswordHandler(
        {
          URL: "/api/user/resetpassword",
          method: "POST",
          header: {
            "Content-Type": "application/json",
          },
          body: {
            resetToken: resetToken,
            password: data.password
          },
        },
        resetPasswordResponse
      );
    }
  };

  return (
    <Fragment>
      <Container
        component="main"
        maxWidth={false}
        disableGutters
        sx={{ height: "100vh" }}
      >
        <Box
          sx={{
            margin: 0,
            height: "100vh",
          }}
        >
          <Grid container>
            <CssBaseline />

            <Grid
              item
              sm={5}
              // spacing={2}
              sx={{
                display: { xs: "none", sm: "flex", lg: "flex" },
                backgroundImage: "url(/assets/Elements/Login-side-panel.png)",
                backgroundRepeat: "no-repeat",
                backgroundColor: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.grey[50]
                    : t.palette.grey[900],
                backgroundSize: "cover",

                backgroundPosition: "center",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SideBanner></SideBanner>
            </Grid>

            <Grid
              item
              xs={12}
              sm={7}
              // md={0}
              component={Paper}
              // elevation={6}
              square
            >
              <Grid
                sx={{
                  mx: 4,
                  display: "flex",
                  height: "100vh",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "Space-between",
                }}
              >
                <Grid>
                  <Typography variant="body2">{""} </Typography>
                </Grid>
                <Grid sx={{ width: "100%" }} xs={12} sm={10} md={8}>
                  {isResetPasswordState === false ? (
                    <Box
                      mt={3}
                      component="form"
                      noValidate
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Typography component="h5" variant="h5">
                        Reset Password
                      </Typography>
                      <Typography
                        variant="body2"
                        mb={2}
                        mt={1}
                        sx={{ color: isError === true ? "#FF0606" : "#C1C1C1" }}
                      >
                        {isError
                          ? message
                          : "Enter the new password"}
                      </Typography>

                      <Divider light={true} />

                      <Typography variant="subtitle2" mb={1}>
                        Password
                      </Typography>
                      <TextField
                        error={Boolean(errors.password)}
                        variant="outlined"
                        helperText={
                          errors.password ? errors.password.message : " "
                        }
                        required
                        fullWidth
                        placeholder="************"
                        {...resetPassword("password")}
                        type={showPassword ? "text" : "password"}
                        id="password"
                        size="small"
                        // sx={{ mb: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff
                                    sx={{
                                      color: "#8D8D8D",
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                ) : (
                                  <Visibility
                                    sx={{
                                      color: "#8D8D8D",
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }} //////////////////////////////
                      />

                      <Typography variant="subtitle2" mb={1}>
                        Confirm Password
                      </Typography>
                      <TextField
                        error={Boolean(errors.passwordConfirmation)}
                        variant="outlined"
                        helperText={
                          errors.passwordConfirmation
                            ? errors.passwordConfirmation.message
                            : " "
                        }
                        required
                        fullWidth
                        placeholder="************"
                        {...resetPassword("passwordConfirmation")}
                        type={showPassword ? "text" : "password"}
                        id="passwordConfirmation"
                        size="small"
                        // sx={{ mb: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff
                                    sx={{
                                      color: "#8D8D8D",
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                ) : (
                                  <Visibility
                                    sx={{
                                      color: "#8D8D8D",
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }} //////////////////////////////
                      />

                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <LoadingButton
                          type="submit"
                          variant="contained"
                          loading={isLoading}
                          sx={{
                            mt: 3,
                            mb: 2,
                            width: "250px",
                            bgcolor: "#F47907",
                            fontSize: "11px",
                            "&.Mui-disabled": {
                              background: "#f6d9b9",
                            },
                            "&:hover": {
                              bgcolor: "#FF8D24",
                            },
                          }}
                        >
                          Reset Password
                        </LoadingButton>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Typography variant="body2" sx={{ color: "gray" }}>
                          
                          <NavLink to="/login">{"Return to "}Login</NavLink>
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Grid sx={{ width: "100%", textAlign: "center" }}>
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <LoadingButton
                            variant="text"
                            sx={{
                              mt: 3,
                              mb: 2,
                              width: "100px",
                              height: "100px",
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{ color: "#5ACE65", fontSize: "60px" }}
                            />
                          </LoadingButton>
                        </Box>

                        <Typography
                          component="h5"
                          variant="h5"
                          sx={{ fontSize: "30px" }}
                        >
                          {"Password reset successful"}
                        </Typography>
                        <Typography
                          variant="body2"
                          mb={2}
                          mt={1}
                          sx={{ color: "gray", fontSize: "16px" }}
                        >
                          {
                            "Your password has been changed successfully."
                          }
                        </Typography>
                        <Divider light={true} />

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <NavLink to="/login">
                            <LoadingButton
                              type="submit"
                              variant="contained"
                              loading={isLoading}
                              sx={{
                                mt: 3,
                                mb: 2,
                                width: "250px",
                                bgcolor: "#F47907",
                                fontSize: "11px",
                                "&.Mui-disabled": {
                                  background: "#f6d9b9",
                                },
                                "&:hover": {
                                  bgcolor: "#FF8D24",
                                },
                              }}
                            >
                              Return to Login
                            </LoadingButton>
                          </NavLink>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Grid mb={2}>
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    If you're facing any issues on this page, Please{" "}
                    <NavLink to="#">contact us</NavLink>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Fragment>
  );
};

export default ResetPassword;
